import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  FreshStrawFlushEmbryoRow,
  useEnsureFreshStrawFlushEmbryosMutation,
  useGetFreshStrawFlushDetailQuery,
  useGetFreshStrawFlushesQuery,
} from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';

import type { FreshStrawSession } from '../../hooks/useFreshStrawSession';
import type { FreshStrawTripContext } from '../../hooks/useFreshStrawTripContext';
import { FreshStrawNoStimNoFlushDialog } from '../FreshStrawNoStimNoFlushDialog';

interface FreshStrawFlushTabProps {
  session: FreshStrawSession;
  tripContext?: FreshStrawTripContext;
}

export function FreshStrawFlushTab({
  session,
  tripContext,
}: FreshStrawFlushTabProps) {
  const [selectedFlushId, setSelectedFlushId] = useState('');
  const [noStimOpen, setNoStimOpen] = useState(false);
  const dispatch = useDispatch();

  const flushesQuery = useGetFreshStrawFlushesQuery(
    {
      flushDate: session.transferDate,
      siteId: session.transferSite,
    },
    { skip: !session.transferDate || !session.transferSite },
  );

  const detailQuery = useGetFreshStrawFlushDetailQuery(
    { flushId: selectedFlushId },
    { skip: !selectedFlushId },
  );

  const [ensureEmbryos, ensureState] =
    useEnsureFreshStrawFlushEmbryosMutation();

  useEffect(() => {
    const flushes = flushesQuery.data ?? [];
    if (!selectedFlushId && flushes.length > 0) {
      setSelectedFlushId(flushes[0].flushId);
    }
  }, [flushesQuery.data, selectedFlushId]);

  const detail = detailQuery.data ?? null;
  const stopIdNum = Number(session.stopId);
  const stopIdForPost =
    Number.isFinite(stopIdNum) && stopIdNum > 0 ? stopIdNum : undefined;
  void tripContext;

  const handleEnsure = async () => {
    if (!selectedFlushId) return;
    try {
      await ensureEmbryos({
        flushId: selectedFlushId,
        stopId: stopIdForPost,
      }).unwrap();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Flush embryos reconciled',
          detail: 'Fresh and frozen embryo counts now match the flush header.',
          life: ToastLife.short,
        }),
      );
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? ((err as { data?: { message?: string } }).data?.message ??
            'Ensure flush embryos failed.')
          : 'Ensure flush embryos failed.';
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Could not reconcile flush',
          detail: message,
          life: ToastLife.long,
        }),
      );
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-white p-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">Flush</label>
          <select
            value={selectedFlushId}
            onChange={(e) => setSelectedFlushId(e.target.value)}
            disabled={flushesQuery.isFetching}
            className="min-w-[240px] rounded border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="">— pick a flush —</option>
            {(flushesQuery.data ?? []).map((f) => (
              <option key={f.flushId} value={f.flushId}>
                {f.name ?? f.flushId}
              </option>
            ))}
          </select>
        </div>
        <Button
          onClick={handleEnsure}
          disabled={!selectedFlushId || ensureState.isLoading}
        >
          {ensureState.isLoading ? 'Reconciling…' : 'Ensure embryos exist'}
        </Button>
        <Button
          variant="outline"
          onClick={() => setNoStimOpen(true)}
          disabled={!selectedFlushId}
        >
          No Stim / No Flush
        </Button>
        {flushesQuery.isError && (
          <div className="text-xs text-red-700">Failed to load flushes.</div>
        )}
      </div>

      <FreshStrawNoStimNoFlushDialog
        open={noStimOpen}
        onOpenChange={setNoStimOpen}
        flushId={selectedFlushId || null}
        flushLabel={detail?.name ?? undefined}
      />

      {!session.transferSite && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Select a transfer site (and date) in the filter panel above to load
          flushes.
        </div>
      )}

      {detail && <FlushHeader detail={detail} />}

      {detail && (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-2">
          <FlushEmbryoGrid
            title={`Fresh (${detail.freshEmbryos.length}/${detail.transferred ?? 0})`}
            rows={detail.freshEmbryos}
          />
          <FlushEmbryoGrid
            title={`Frozen (${detail.frozenEmbryos.length}/${detail.frozen ?? 0})`}
            rows={detail.frozenEmbryos}
          />
        </div>
      )}
    </div>
  );
}

interface FlushHeaderProps {
  detail: {
    name?: string | null;
    donorName?: string | null;
    sireName?: string | null;
    ownerCode?: string | null;
    totalCollected?: number | null;
    transferred?: number | null;
    frozen?: number | null;
    unFertilized?: number | null;
    degenerates?: number | null;
    grade3?: number | null;
    leftCl?: number | null;
    rightCl?: number | null;
    comment?: string | null;
  };
}

function FlushHeader({ detail }: FlushHeaderProps) {
  return (
    <div className="rounded border border-gray-200 bg-white p-3 text-sm">
      <div className="mb-2 flex items-baseline gap-2">
        <div className="text-base font-semibold text-gray-800">
          {detail.name ?? 'Flush'}
        </div>
        {detail.donorName && (
          <div className="text-xs text-gray-500">Donor: {detail.donorName}</div>
        )}
        {detail.sireName && (
          <div className="text-xs text-gray-500">Sire: {detail.sireName}</div>
        )}
        {detail.ownerCode && (
          <div className="text-xs text-gray-500">Owner: {detail.ownerCode}</div>
        )}
      </div>
      <div className="grid grid-cols-4 gap-x-3 gap-y-1 text-xs">
        <HeaderStat label="Total" value={detail.totalCollected} />
        <HeaderStat label="Transfer" value={detail.transferred} />
        <HeaderStat label="Freeze" value={detail.frozen} />
        <HeaderStat label="UFO" value={detail.unFertilized} />
        <HeaderStat label="Degen" value={detail.degenerates} />
        <HeaderStat label="G3" value={detail.grade3} />
        <HeaderStat label="CL L" value={detail.leftCl} />
        <HeaderStat label="CL R" value={detail.rightCl} />
      </div>
      {detail.comment && (
        <div className="mt-2 text-xs text-gray-600 italic">
          {detail.comment}
        </div>
      )}
    </div>
  );
}

function HeaderStat({
  label,
  value,
}: {
  label: string;
  value?: number | null;
}) {
  return (
    <div>
      <span className="text-gray-500">{label}:</span>{' '}
      <span className="font-medium text-gray-800">{value ?? 0}</span>
    </div>
  );
}

function FlushEmbryoGrid({
  title,
  rows,
}: {
  title: string;
  rows: FreshStrawFlushEmbryoRow[];
}) {
  return (
    <div className="flex min-h-0 flex-col overflow-hidden rounded border border-gray-200 bg-white">
      <div className="border-b border-gray-200 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-600">
        {title}
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {rows.length === 0 ? (
          <div className="p-3 text-xs text-gray-500">No rows.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-100 text-left text-[11px] font-semibold text-gray-600">
              <tr>
                <th className="px-2 py-1">Tag</th>
                <th className="px-2 py-1">Straw</th>
                <th className="px-2 py-1">Stage</th>
                <th className="px-2 py-1">Grade</th>
                <th className="px-2 py-1">Status</th>
                <th className="px-2 py-1">Comment</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.embryoId}
                  className="border-t border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-2 py-1">{r.embTagNumber ?? ''}</td>
                  <td className="px-2 py-1">{r.strawNumber ?? ''}</td>
                  <td className="px-2 py-1">{r.stage ?? ''}</td>
                  <td className="px-2 py-1">{r.grade ?? ''}</td>
                  <td className="px-2 py-1">{r.status ?? ''}</td>
                  <td className="px-2 py-1">{r.comm ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
